import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { WorkspaceSummary } from '@mailpilot/shared';
import { api } from '../lib/api';
import { qk } from '../lib/keys';
import { useToast } from '../components/ui/Toast';
import { useMe } from './useMe';

/** The workspace this device is working in, plus every workspace the person belongs to. */
export function useCurrentWorkspace(): { workspace: WorkspaceSummary | undefined; workspaces: WorkspaceSummary[]; isOrg: boolean; isAdmin: boolean } {
  const me = useMe().data;
  const workspace = me?.workspace;
  return {
    workspace,
    workspaces: me?.workspaces ?? [],
    isOrg: workspace?.kind === 'organization',
    isAdmin: workspace?.role === 'owner' || workspace?.role === 'admin',
  };
}

/** Members, divisions and invites of the current workspace. */
export function useWorkspaceDetail(enabled = true) {
  return useQuery({ queryKey: qk.workspace, queryFn: () => api('GET /api/workspace'), enabled, staleTime: 30_000 });
}

/**
 * Everything cached belongs to the previous workspace: drop it, reload who-am-I and go to the inbox.
 * (The realtime stream reconnects because it is keyed on the workspace id.)
 */
export function useEnterWorkspace() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useCallback(
    async (to = '/inbox') => {
      qc.clear();
      await qc.fetchQuery({ queryKey: qk.me, queryFn: () => api('GET /api/auth/me') });
      navigate(to, { replace: true });
    },
    [qc, navigate],
  );
}

export function useSwitchWorkspace() {
  const enter = useEnterWorkspace();
  const toast = useToast();
  return useMutation({
    mutationFn: (id: string) => api('POST /api/workspaces/:id/switch', { params: { id } }),
    onSuccess: async (r) => {
      await enter();
      toast.success(`Switched to ${r.workspace.name}`);
    },
    onError: (e) => toast.error(e),
  });
}

export function useCreateOrganization() {
  const enter = useEnterWorkspace();
  return useMutation({
    mutationFn: (b: { name: string; color?: string; divisions?: string[] }) => api('POST /api/workspaces', { body: b }),
    onSuccess: () => enter('/settings/organization'),
  });
}

export function useJoinOrganization() {
  const enter = useEnterWorkspace();
  return useMutation({
    mutationFn: (code: string) => api('POST /api/workspaces/join', { body: { code } }),
    onSuccess: () => enter(),
  });
}

export function useOpenPersonalWorkspace() {
  const enter = useEnterWorkspace();
  const toast = useToast();
  return useMutation({
    mutationFn: () => api('POST /api/workspaces/personal'),
    onSuccess: () => enter(),
    onError: (e) => toast.error(e),
  });
}

/** Shareable join link for an invite code, or null inside the desktop app (share the code instead). */
export function inviteLink(code: string): string | null {
  if (!window.location.protocol.startsWith('http')) return null;
  return `${window.location.origin}/join/${encodeURIComponent(code)}`;
}
